import { LOWER_UUID_PATTERN } from "./kernel/kernel-internal.js";
import { RESOURCE_SLUG_PATTERN } from "./slug-grammar.js";
import { APP_POOL_REGION_PATTERN } from "./region-grammar.js";
import { readFixtureInfrastructure } from "./fixture-resources.js";
import { safeRuleLabel } from "./fixture-placement-rules.js";
import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/blob-buckets.ts
/**
* Blob buckets: the app declaration, the fixture binding, the physical name, the labels and the
* publish rules (2026-10-13-blob-buckets, stage 1 contracts, `overview.md` sections 2.2 and 7).
*
* An app declares its logical buckets once, at app level, in `app.json` `blobStorage.buckets[]`
* (D10). A fixture, or an in-mesh pool through its `infrastructure` block, binds a declared bucket
* by name with a mode: `infrastructure.blobStorage.buckets: { <logical>: 'read' | 'readWrite' }`.
* The legacy fixture form `infrastructure.blobStorage: { bucket, maxSizeGb? }` stays valid and
* means one logical bucket with `readWrite`. An out-of-mesh pool names the one bucket that receives
* its attempt output with the top-level key `blobStorage: { buckets: { <logical>: 'readWrite' } }`
* (2026-10-22-pool-attempt-blob-reads, WS-1), and the publish rules read it as a binder too.
*
* The platform keeps one physical bucket per (data plane, org, app, logical bucket) (D9). Its name
* is an opaque hash from {@link physicalBucketName} (D11). No other code composes a physical name
* (defaults row F10). The labels from {@link blobBucketLabels} carry the readable identity.
*
* The JSON twins are `cloud/platform-sdk/schemas/app-manifest.schema.json` (root `blobStorage`,
* `$defs/blobBucketDeclaration`) and `fixture.schema.json`
* (`$defs/fixtureInfrastructure/properties/blobStorage`). `__tests__/blob-buckets-schema-twin.test.ts`
* runs one sample set through Ajv on each node and through zod on each schema here.
*
* The org data location (stage 3a, R-065): {@link dataLocationSchema}, {@link allowedLocationsSchema}
* and {@link resolveDataLocation} decide where the new buckets of one org live on its data plane.
*
* Pure and browser-safe: SHA-256 comes from `@noble/hashes`. No clock, no filesystem, no network.
*/
/**
* Who shares one physical bucket (`overview.md` section 2.1). `org`, the default: one bucket per
* org that adopted the app, which only that app's identity reaches. `global`: one bucket per app in
* the CP project, shared by every org that consented; it needs the kernel right
* {@link BLOB_BUCKET_GLOBAL_KERNEL_RIGHT} (D3, D7). A `dp` scope does not exist (D16).
*/
const BLOB_BUCKET_SCOPES = ["org", "global"];
/**
* Where an `org` bucket lives (section 2.4). `central`, the default: at the org data location, or
* at the location the org admin picked at adoption. `local`: at the workload location; legal only
* when every binder resolves to one placement (rule `blob-bucket-local-single-placement`).
*/
const BLOB_BUCKET_LOCALITIES = ["central", "local"];
/**
* What a loss of the data costs (section 2.2). `durable`, the default: a relocation copies it.
* `cache`: a relocation deletes the bucket and makes a new empty one at the target (section 2.6).
*/
const BLOB_BUCKET_DURABILITIES = ["durable", "cache"];
/**
* The provider-neutral storage class (section 2.2). The default is `hot`. Each composition maps a
* member to its provider's class (WS-3).
*/
const BLOB_BUCKET_STORAGE_CLASSES = [
	"hot",
	"cool",
	"cold",
	"archive"
];
/**
* The access a fixture or a pool binds a bucket with. `read` gives the app identity
* `objectViewer`; `readWrite` gives it `objectAdmin` (the `BlobBucket` XRD `members[].role`).
*/
const BLOB_BUCKET_MODES = ["read", "readWrite"];
/**
* The grammar of a logical bucket name: the fixture bucket grammar of `fixtureInfrastructureSchema`
* (GCS rules with no dots). 3 to 63 characters of lowercase letters, numbers, hyphens and
* underscores; the first and the last are a letter or a number. Apply
* {@link BLOB_BUCKET_NAME_MAX_LENGTH} before the pattern so the match input is bounded.
*/
const BLOB_BUCKET_NAME_PATTERN = /^[a-z0-9][a-z0-9_-]{1,61}[a-z0-9]$/;
/** The shortest logical bucket name. */
const BLOB_BUCKET_NAME_MIN_LENGTH = 3;
/** The longest logical bucket name. */
const BLOB_BUCKET_NAME_MAX_LENGTH = 63;
/** The most logical buckets one app declares, and the most one fixture or pool binds. */
const BLOB_BUCKETS_PER_APP_MAX = 16;
/** The most browser origins one bucket declares. */
const BLOB_BUCKET_BROWSER_ORIGINS_MAX = 16;
/** The longest browser origin, in characters: `https://`, a 253-character host and `:65535`. */
const BLOB_BUCKET_BROWSER_ORIGIN_MAX_LENGTH = 267;
/**
* The grammar of a browser origin: `https://`, a lowercase DNS host of one or more labels, and an
* optional port of 1 to 5 digits. No path, no query, no fragment, no user info and no trailing
* slash. A label holds no dot, so the repetition is linear (no ReDoS).
*/
const BLOB_BUCKET_BROWSER_ORIGIN_PATTERN = /^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*(?::[0-9]{1,5})?$/;
/** The most lifecycle rules one bucket declares. */
const BLOB_BUCKET_LIFECYCLE_RULES_MAX = 16;
/** The longest lifecycle prefix, in characters. */
const BLOB_BUCKET_LIFECYCLE_PREFIX_MAX_LENGTH = 256;
/**
* The grammar of a lifecycle prefix: 1 to 256 characters from the S3 "safe characters" set
* (letters, numbers, `! - _ . * ' ( )`) plus `/`. Both providers match a prefix byte for byte.
*/
const BLOB_BUCKET_LIFECYCLE_PREFIX_PATTERN = /^[A-Za-z0-9!_.*'()/-]{1,256}$/;
/** The longest `deleteAfterDays` of a lifecycle rule: ten years of 365 days. */
const BLOB_BUCKET_DELETE_AFTER_DAYS_MAX = 3650;
/**
* The grammar of a bucket location: `APP_POOL_REGION_PATTERN`, the one lowercase region grammar of
* the contracts (defaults row F2). It admits a region (`us-central1`, `us-east-1`) and a
* multi-region (`us`, `eu`). The grammar says nothing about whether the provider offers the
* location; the allowed list per data plane decides that (stage 3).
*/
const BLOB_BUCKET_LOCATION_PATTERN = APP_POOL_REGION_PATTERN;
/**
* The kernel right a `scope: global` bucket needs (D3): `storage.global`, R16 and the last name of
* `KERNEL_RIGHTS` since stage 5 (R-094 item 2). Its `KERNEL_RIGHT_CATALOG` row has
* `appGrantable: true`, so a site admin can grant it.
*
* Who reads it: the publish rule {@link checkBlobBucketGlobalRequiresRight}, which refuses a
* `global` declaration when `kernelRights` does not name it, and the control plane, which
* allocates a `global` bucket only for an app that holds an approved request for it with a bucket
* location (R-094 items 3 and 5). The `satisfies` clause fails the compile if the name ever leaves
* `KERNEL_RIGHTS`.
*/
const BLOB_BUCKET_GLOBAL_KERNEL_RIGHT = "storage.global";
/** The logical name of the per-org platform bucket in {@link physicalBucketName}. */
const BLOB_BUCKET_PLATFORM_LOGICAL_NAME = "platform";
/**
* The logical name of the reserved claim-check bucket (2026-10-13-blob-buckets, R-102, R-104):
* `claimcheck`.
*
* What it names. Every app that declares `platform.eventBus` gets one org-app bucket of this
* logical name per (data plane, org, app). The app declares nothing: release-engine adds the entry
* in `declaredBlobBuckets` after the cap loop of 16, so it never counts against
* {@link BLOB_BUCKETS_PER_APP_MAX} (R-102 item 1). Its declaration is built in: `scope: org`,
* `locality: local`, `durability: durable`, role `objectAdmin` (R-102 item 2). Its physical name
* comes from {@link physicalBucketName} with this logical name, like any org-app bucket
* (defaults row F10). The bucket never enters `bindings.json`; its bindings travel in the
* claim-check bindings file of `@zion/sdk-cp-dp-contracts` (R-104 item 3).
*
* Who reads it: the publish rule {@link checkBlobBucketReservedName}, which refuses the name in an
* app declaration and in a legacy fixture binding (R-102 item 3); release-engine, which adds the
* reserved entry; the SDK claim-check writers, which name the bucket in their errors. The value is
* inside the grammar of {@link blobBucketNameSchema}.
*/
const BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME = "claimcheck";
/**
* The two per-org platform buckets of one org on one data plane (2026-10-13-blob-buckets, R-027).
* Each is the `logical` of an {@link OrgPlatformBucketIdentity}, and the `zion_kind` label of the
* bucket ({@link blobBucketLabels}).
*
* - `platform`: mail attachments, work-run results and blob-proxy attempts. The members are the
*   platform accounts of core-api, the operator program and the blob proxy, each `objectUser`
*   (R-029 item 2). The allocator deletes it 30 days after the org leaves the data plane (R-028).
* - `audit`: the audit archive. A create-only writer and a reader, a 365-day retention, no soft
*   delete, and `deletionPolicy: Orphan` (R-029 item 3). The allocator never deletes it.
*
* Two buckets and not one: a bucket-level retention applies to every object of the bucket, so one
* shared bucket would block the deletes of mail attachments and work-run results (R-027).
* Every app account stays out of both (D1, R-029 item 4).
*/
const PLATFORM_BUCKET_KINDS = ["platform", "audit"];
/** The first two characters of every physical bucket name. */
const PHYSICAL_BUCKET_NAME_PREFIX = "zb";
/** The number of base32 characters after {@link PHYSICAL_BUCKET_NAME_PREFIX}. */
const PHYSICAL_BUCKET_NAME_HASH_LENGTH = 30;
/**
* The grammar of a physical bucket name: `zb` and 30 characters of the lowercase RFC 4648 base32
* alphabet, 32 characters in all. It is a valid GCS name and a valid S3 name.
*/
const PHYSICAL_BUCKET_NAME_PATTERN = /^zb[a-z2-7]{30}$/;
/** The highest bucket generation: the largest PostgreSQL `integer`. */
const BLOB_BUCKET_GENERATION_MAX = 2147483647;
/**
* The three kinds of bucket (`overview.md` section 2.1), the answer of {@link blobBucketKind}.
* `org-app`: one per (data plane, org, app, logical bucket). `org-platform`: one per (data plane,
* org, {@link PlatformBucketKind}), for platform identities only. `global-app`: one per (control
* plane, app, logical bucket), in the CP project.
*
* The `zion_kind` label carries the kind for `org-app` and `global-app`, and the
* {@link PlatformBucketKind} (`platform` or `audit`) for an `org-platform` bucket
* ({@link blobBucketLabels}, `00-index.md` stage 2a contracts item 1).
*/
const BLOB_BUCKET_KINDS = [
	"org-app",
	"org-platform",
	"global-app"
];
/** The keys {@link blobBucketLabels} may emit, in emission order. */
const BLOB_BUCKET_LABEL_KEYS = [
	"dp_id",
	"org_id",
	"app",
	"bucket",
	"zion_kind"
];
/** The longest GCS label value. */
const BLOB_BUCKET_LABEL_VALUE_MAX_LENGTH = 63;
/** The longest app slug: the `appSlug` cap of `configs/xrd-app.yaml`. */
const APP_SLUG_MAX_LENGTH = 64;
/** A logical bucket name in {@link BLOB_BUCKET_NAME_PATTERN}. The cap runs before the pattern. */
const blobBucketNameSchema = z.string().min(3).max(63).regex(BLOB_BUCKET_NAME_PATTERN, "a bucket name of lowercase letters, numbers, hyphens and underscores");
/** One member of {@link BLOB_BUCKET_MODES}; every other value fails. */
const blobBucketModeSchema = z.enum(BLOB_BUCKET_MODES);
/**
* One lifecycle rule: delete an object `deleteAfterDays` days after its creation, for every object
* whose key starts with `prefix`, or for every object when `prefix` is absent. `deleteAfterDays` is
* an integer from 1 to {@link BLOB_BUCKET_DELETE_AFTER_DAYS_MAX}. Strict: an unknown key fails.
*/
const blobBucketLifecycleRuleSchema = z.object({
	prefix: z.string().max(256).regex(BLOB_BUCKET_LIFECYCLE_PREFIX_PATTERN, "a prefix of S3 safe characters and /").optional(),
	deleteAfterDays: z.number().int().min(1).max(BLOB_BUCKET_DELETE_AFTER_DAYS_MAX)
}).strict();
/**
* Browser access (section 2.2): `origins` are the 1 to {@link BLOB_BUCKET_BROWSER_ORIGINS_MAX}
* distinct origins the bucket CORS rule admits, each in {@link BLOB_BUCKET_BROWSER_ORIGIN_PATTERN};
* `upload: true` admits `PUT` from those origins as well as `GET`. `upload` defaults to `false`.
* Absent `browser` means no CORS rule. Strict: an unknown key fails.
*/
const blobBucketBrowserSchema = z.object({
	origins: z.array(z.string().max(267).regex(BLOB_BUCKET_BROWSER_ORIGIN_PATTERN, "an https origin with no path")).min(1).max(16).refine((origins) => new Set(origins).size === origins.length, { message: "origins must be unique" }),
	upload: z.boolean().default(false)
}).strict();
/**
* One logical bucket of an app (section 2.2). Strict: an unknown key fails.
*
* Inputs and defaults: `name` is required ({@link blobBucketNameSchema}). `scope` defaults to
* `org`, `locality` to `central`, `durability` to `durable`, `versioning` to `false`,
* `storageClass` to `hot` and `events` to `false`. `lifecycle` (at most
* {@link BLOB_BUCKET_LIFECYCLE_RULES_MAX} rules), `maxSizeGb` (an integer of 1 or more) and
* `browser` stay absent when the author omits them.
*
* Cross-field refusal: `scope: global` with `locality: local` fails at `locality`. A `global`
* bucket lives in the CP project at a location the site admin approved (D7), so it has no workload
* location. The JSON twin carries the same refusal as an `if`/`then`.
*
* Output: the declaration with every default applied ({@link ResolvedBlobBucketDeclaration}).
*/
const blobBucketDeclarationSchema = z.object({
	name: blobBucketNameSchema,
	scope: z.enum(BLOB_BUCKET_SCOPES).default("org"),
	locality: z.enum(BLOB_BUCKET_LOCALITIES).default("central"),
	durability: z.enum(BLOB_BUCKET_DURABILITIES).default("durable"),
	versioning: z.boolean().default(false),
	lifecycle: z.array(blobBucketLifecycleRuleSchema).max(16).optional(),
	storageClass: z.enum(BLOB_BUCKET_STORAGE_CLASSES).default("hot"),
	maxSizeGb: z.number().int().min(1).optional(),
	browser: blobBucketBrowserSchema.optional(),
	events: z.boolean().default(false)
}).strict().superRefine((d, ctx) => {
	if (d.scope === "global" && d.locality === "local") ctx.addIssue({
		code: "custom",
		path: ["locality"],
		message: "a global bucket has no workload location"
	});
});
/**
* The app-level `blobStorage` key of `app.json`: `{ buckets }`, 1 to
* {@link BLOB_BUCKETS_PER_APP_MAX} declarations with distinct names. A repeated name fails at
* `buckets/<index>/name` of the second one. The JSON twin cannot express the uniqueness, so the
* publish rule `blob-bucket-name-unique` carries it at the doors that run Ajv only (defaults row F7).
* Strict: an unknown key fails.
*/
const appBlobStorageSchema = z.object({ buckets: z.array(blobBucketDeclarationSchema).min(1).max(16) }).strict().superRefine((value, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	value.buckets.forEach((bucket, index) => {
		if (seen.has(bucket.name)) ctx.addIssue({
			code: "custom",
			path: [
				"buckets",
				index,
				"name"
			],
			message: "bucket names must be unique"
		});
		seen.add(bucket.name);
	});
});
/**
* The legacy fixture binding `{ bucket, maxSizeGb? }`: one logical bucket named `bucket` with
* `readWrite`. With no app declaration of that name it is an implicit declaration with every
* default (WS-1 `resolveFixtureBucketBindings`). Strict.
*/
const legacyFixtureBlobStorageSchema = z.object({
	bucket: blobBucketNameSchema,
	maxSizeGb: z.number().int().min(1).optional()
}).strict();
/**
* The named fixture binding `{ buckets: { <logical>: <mode> } }`: 1 to
* {@link BLOB_BUCKETS_PER_APP_MAX} logical names, each in {@link blobBucketNameSchema}, each with a
* {@link BlobBucketMode}. Every name must name a bucket the app declares (rule
* `blob-bucket-binding-declared`). `maxSizeGb` belongs to the app declaration in this form, so this
* object refuses it. Strict.
*/
const namedFixtureBlobStorageSchema = z.object({ buckets: z.record(blobBucketNameSchema, blobBucketModeSchema).refine((buckets) => Object.keys(buckets).length >= 1, { message: "bind at least one bucket" }).refine((buckets) => Object.keys(buckets).length <= 16, { message: `bind at most 16 buckets` }) }).strict();
/**
* The fixture key `infrastructure.blobStorage` (and the deprecated `resources.blobStorage`): exactly
* one of the legacy form or the named form. An object with both `bucket` and `buckets`, or with
* neither, fails both arms. The worker boolean `workers[].blobStorage` is a separate key and stays.
*/
const fixtureBlobStorageBindingSchema = z.union([legacyFixtureBlobStorageSchema, namedFixtureBlobStorageSchema]);
function isLowerUuid(value) {
	return typeof value === "string" && LOWER_UUID_PATTERN.test(value);
}
function isAppSlug(value) {
	return typeof value === "string" && value.length >= 2 && value.length <= APP_SLUG_MAX_LENGTH && RESOURCE_SLUG_PATTERN.test(value);
}
function isLogicalName(value) {
	return typeof value === "string" && value.length <= 63 && BLOB_BUCKET_NAME_PATTERN.test(value);
}
function isPlatformBucketKind(value) {
	return PLATFORM_BUCKET_KINDS.includes(value);
}
/** A lower-case RFC 9562 version 4 UUID: the version nibble `4` and the variant nibble `8` to `b`. */
const LOWER_UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
function isControlPlaneId(value) {
	return typeof value === "string" && LOWER_UUID_V4_PATTERN.test(value);
}
/** The `controlPlaneId` of `identity`, or `undefined` when the object has no such key. */
function controlPlaneIdOf(identity) {
	return "controlPlaneId" in identity ? identity.controlPlaneId : void 0;
}
/**
* The kind of the bucket that `identity` names. Pure.
*
* - `org-app`: `dpId` and `orgId` are lower-case UUIDs, `appSlug` is an app slug and `logical` is a
*   logical bucket name.
* - `org-platform`: `dpId` and `orgId` are lower-case UUIDs, `appSlug` is `null` and `logical` is
*   a member of {@link PLATFORM_BUCKET_KINDS} (`platform` or `audit`).
* - `global-app`: `dpId` and `orgId` are `null`, `appSlug` is an app slug, `logical` is a
*   logical bucket name and `controlPlaneId` is a lower-case version 4 UUID.
*
* Throws `TypeError` for every other combination: an upper-case UUID, an empty string in place of
* `null`, one of `dpId` and `orgId` `null` and the other not, an invalid slug or name, and a
* platform bucket with a logical name outside {@link PLATFORM_BUCKET_KINDS}. It also throws for a global identity
* with no `controlPlaneId` or with a value outside the version 4 grammar, and for an org identity
* that carries a `controlPlaneId` other than `undefined`. The message names the first field at
* fault, and never echoes the `controlPlaneId` value. The refusal keeps one identity to one hash:
* `ABC` and `abc` would hash apart, and an org name does not read `controlPlaneId`.
*/
function blobBucketKind(identity) {
	const { dpId, orgId, appSlug, logical } = identity;
	const controlPlaneId = controlPlaneIdOf(identity);
	if (dpId === null && orgId === null) {
		if (!isAppSlug(appSlug)) throw new TypeError(`blobBucketKind: appSlug ${JSON.stringify(appSlug)} is not an app slug`);
		if (!isLogicalName(logical)) throw new TypeError(`blobBucketKind: logical ${JSON.stringify(logical)} is not a bucket name`);
		if (!isControlPlaneId(controlPlaneId)) throw new TypeError("blobBucketKind: a global bucket needs controlPlaneId, a lower-case version 4 UUID");
		return "global-app";
	}
	if (controlPlaneId !== void 0) throw new TypeError("blobBucketKind: controlPlaneId is only for a global bucket, and this identity has a dpId or an orgId");
	if (!isLowerUuid(dpId)) throw new TypeError(`blobBucketKind: dpId ${JSON.stringify(dpId)} is not a lower-case UUID`);
	if (!isLowerUuid(orgId)) throw new TypeError(`blobBucketKind: orgId ${JSON.stringify(orgId)} is not a lower-case UUID`);
	if (appSlug === null) {
		if (!isPlatformBucketKind(logical)) throw new TypeError(`blobBucketKind: a platform bucket has logical "platform" or "audit", not ${JSON.stringify(logical)}`);
		return "org-platform";
	}
	if (!isAppSlug(appSlug)) throw new TypeError(`blobBucketKind: appSlug ${JSON.stringify(appSlug)} is not an app slug`);
	if (!isLogicalName(logical)) throw new TypeError(`blobBucketKind: logical ${JSON.stringify(logical)} is not a bucket name`);
	return "org-app";
}
const BASE32_ALPHABET = "abcdefghijklmnopqrstuvwxyz234567";
/**
* The first `length` characters of the lowercase RFC 4648 base32 text of `bytes`, no padding. It
* reads only the `ceil(length * 5 / 8)` bytes those characters need; `bytes` must hold that many.
*/
function base32Prefix(bytes, length) {
	let out = "";
	let buffer = 0;
	let bits = 0;
	for (const byte of bytes.subarray(0, Math.ceil(length * 5 / 8))) {
		buffer = (buffer << 8 | byte) & 4095;
		bits += 8;
		while (bits >= 5) {
			out += BASE32_ALPHABET.charAt(buffer >>> bits - 5 & 31);
			bits -= 5;
		}
	}
	return out.slice(0, length);
}
/**
* The physical bucket name of `key` (D11, defaults row F10). The only function that composes one.
*
* 1. Check `key` with {@link blobBucketKind}, and check that `generation` is an integer from 1 to
*    {@link BLOB_BUCKET_GENERATION_MAX}.
* 2. Join `dpId`, `orgId`, `appSlug`, `logical` and the decimal `generation` with `/`, with the
*    empty string for a `null` field. For a `global-app` key, append `controlPlaneId` as a sixth
*    field. No field holds a `/`, so the join is unambiguous, and a global text (six fields, empty
*    `orgId`) never equals an org text (five fields, a UUID `orgId`).
* 3. Take SHA-256 of the UTF-8 bytes, encode it as lowercase RFC 4648 base32 and keep the first 30
*    characters.
* 4. Return `zb` and those 30 characters: 32 characters in {@link PHYSICAL_BUCKET_NAME_PATTERN}.
*
* Equal keys give equal names, on every caller and every run. An org key reads only its five
* fields, so its name holds on every control plane: the random `dpId` keeps it apart. A global key
* on two control planes gives two names, and a party that does not hold `controlPlaneId` cannot
* compute the name from the public slug and logical name. Throws `TypeError` for every key that
* {@link blobBucketKind} refuses, and for a generation that is not an integer in range. Pure.
*/
function physicalBucketName(key) {
	const kind = blobBucketKind(key);
	const { generation } = key;
	if (!Number.isInteger(generation) || generation < 1 || generation > 2147483647) throw new TypeError(`physicalBucketName: generation ${JSON.stringify(generation)} is not an integer from 1 to ${BLOB_BUCKET_GENERATION_MAX}`);
	const fields = [
		key.dpId ?? "",
		key.orgId ?? "",
		key.appSlug ?? "",
		key.logical,
		String(generation)
	];
	if (kind === "global-app") fields.push(String(controlPlaneIdOf(key)));
	const text = fields.join("/");
	return "zb" + base32Prefix(sha256(utf8ToBytes(text)), 30);
}
/**
* The provider labels of the bucket that `identity` names (section 2.3), keyed in the order of
* {@link BLOB_BUCKET_LABEL_KEYS}:
*
* - `dp_id` and `org_id`: present for `org-app` and `org-platform`, absent for `global-app`.
* - `app`: the app slug cut to {@link BLOB_BUCKET_LABEL_VALUE_MAX_LENGTH} characters; absent for
*   `org-platform`.
* - `bucket`: the logical name.
* - `zion_kind`: the {@link BlobBucketKind} for `org-app` and `global-app`; for `org-platform`, the
*   {@link PlatformBucketKind}, `platform` or `audit` (stage 2a contracts item 1). So a label
*   filter on `zion_kind=audit` finds every audit bucket and no other.
*
* Every value is in the GCS label grammar (lowercase letters, numbers, `-` and `_`, at most 63
* characters), because {@link blobBucketKind} admits only such inputs. Throws `TypeError` where
* {@link blobBucketKind} throws. Pure; the result is a new frozen object.
*/
function blobBucketLabels(identity) {
	const kind = blobBucketKind(identity);
	const labels = {};
	if (identity.dpId !== null) labels.dp_id = identity.dpId;
	if (identity.orgId !== null) labels.org_id = identity.orgId;
	if (identity.appSlug !== null) labels.app = identity.appSlug.slice(0, 63);
	labels.bucket = identity.logical;
	labels.zion_kind = kind === "org-platform" ? identity.logical : kind;
	return Object.freeze(labels);
}
/** The rule ids of the blob bucket publish rules, in the order {@link checkBlobBucketRules} runs them. Gate names: never renamed for prose. */
const BLOB_BUCKET_RULE_IDS = [
	"blob-bucket-name-unique",
	"blob-bucket-binding-declared",
	"blob-bucket-global-requires-right",
	"blob-bucket-local-single-placement",
	"blob-bucket-reserved-name"
];
/**
* The messages of the blob bucket rules, as data, one per rule id. `APP_POOL_RULE_MESSAGES` spreads
* this table, so the register of `checkAppPools` and the id list stay one table (DES-4, T-27a).
*
* Each message names the field and the value the rule read, says what breaks, and ends with one
* instruction. Every argument arrives sanitised: the rule passes each app-authored value through
* `safeRuleLabel` before it builds the arguments (the M-1 defence). Pure.
*/
const BLOB_BUCKET_RULE_MESSAGES = {
	"blob-bucket-name-unique": ({ bucket, first }) => `blobStorage.buckets declares bucket '${bucket}' again, and buckets[${first}] is its first declaration. The platform keeps one physical bucket per logical name, so the two declarations cannot both apply. Remove one declaration, or give it another name.`,
	"blob-bucket-binding-declared": ({ bucket }) => `This binding names bucket '${bucket}', and app.json declares no bucket of that name in blobStorage.buckets. The platform makes a bucket only for a declaration, so the binding reaches no bucket at run time. Declare '${bucket}' in app.json, or bind a declared bucket.`,
	"blob-bucket-global-requires-right": ({ bucket, right }) => `Bucket '${bucket}' declares scope 'global', and kernelRights does not name '${right}'. A global bucket is shared by every org that adopts the app, so the platform makes one only for an app that holds the right. Add '${right}' to kernelRights, or use scope 'org'.`,
	"blob-bucket-local-single-placement": ({ bucket, count, placements }) => `Bucket '${bucket}' declares locality 'local', and its binders run in ${count} placements (${placements}). A local bucket lives at one workload location, so the platform cannot put it beside every binder. Bind it from one placement, or use locality 'central'.`,
	"blob-bucket-reserved-name": ({ bucket }) => `This entry names bucket '${bucket}', and the platform reserves that name for the claim-check bucket of every app with platform.eventBus. The platform keeps one physical bucket per logical name, so your objects and the platform's event payloads would share one bucket. Give the bucket another name.`
};
/**
* The placement of every binder that runs at the data plane home: every fixture that runs a
* workload at home, and every pool with no `region`. A pool with a `region` string has that region
* as its placement. A fixture whose only runnable surface is `poolImage: true` runs only as the
* image of its pools, so it has no placement of its own.
*/
const BLOB_BUCKET_HOME_PLACEMENT = "home";
function asRecord(v) {
	return typeof v === "object" && v !== null && !Array.isArray(v) ? v : null;
}
/** One untrusted value as a JSON Pointer segment: the M-1 defence, then RFC 6901 escapes. */
function pointerSegment(raw) {
	return safeRuleLabel(raw).replaceAll("~", "~0").replaceAll("/", "~1");
}
function issue(rule, path, message) {
	return {
		severity: "error",
		rule,
		path,
		message
	};
}
/** The declared bucket entries of an app, each with its index; entries that are not objects are skipped. */
function declaredEntries(app) {
	const buckets = asRecord(app["blobStorage"])?.["buckets"];
	if (!Array.isArray(buckets)) return [];
	const out = [];
	buckets.forEach((raw, index) => {
		const entry = asRecord(raw);
		if (entry !== null) out.push({
			index,
			entry
		});
	});
	return out;
}
/** `true` when `value` is an array with at least one member. */
function nonEmptyArray(value) {
	return Array.isArray(value) && value.length > 0;
}
/**
* `true` when the fixture runs a workload at the data plane home. A fixture without
* `poolImage: true` always does. A `poolImage` fixture does only when it also declares another
* runnable surface of the `#413 / D8` clause of `fixture.schema.json`: a non-empty `services`,
* `workers` or `jobs`, or a non-empty `targets` together with a non-empty `triggers`. Otherwise it
* runs only as the image of the pools that name it, and each such pool counts as its own binder.
*
* Input: a fixture manifest that is a plain object; nothing in it is parsed. Pure; never throws.
*
* Two callers read it. The binders of this module take a placement from it. `checkAppPools` in
* `app-pool-rules.ts` takes from it the fixtures that can read the attempt output of an out-of-mesh
* pool (`pool-blob-bucket-exceeds-fixtures`). The package barrel `index.ts` does not re-export it,
* so it is not part of the public surface of `@zion/sdk-contracts`.
*/
function runsAtHome(m) {
	if (m["poolImage"] !== true) return true;
	return nonEmptyArray(m["services"]) || nonEmptyArray(m["workers"]) || nonEmptyArray(m["jobs"]) || nonEmptyArray(m["targets"]) && nonEmptyArray(m["triggers"]);
}
/**
* Every fixture and every pool of the bundle that holds a `blobStorage` object, fixtures first.
*
* A pool binds through two keys. Any pool may carry `infrastructure.blobStorage`, the in-mesh form,
* at `/pools/<pool>/infrastructure/blobStorage`. A pool whose `workload` is `out-of-mesh` may also
* carry the top-level `blobStorage`, at `/pools/<pool>/blobStorage`
* (2026-10-22-pool-attempt-blob-reads, WS-1). A pool that holds both gives two binders, the
* `infrastructure` one first. Both carry the placement of the pool.
*/
function binders(bundle, app) {
	const out = [];
	for (const { fixtureId, manifest } of bundle.fixtures) {
		const m = asRecord(manifest);
		if (m === null) continue;
		const blobStorage = asRecord(asRecord(readFixtureInfrastructure(m))?.["blobStorage"]);
		if (blobStorage === null) continue;
		const key = (m["infrastructure"] ?? null) === null ? "resources" : "infrastructure";
		out.push({
			pointer: `fixtures/${pointerSegment(fixtureId)}/${key}/blobStorage`,
			placement: runsAtHome(m) ? BLOB_BUCKET_HOME_PLACEMENT : null,
			blobStorage
		});
	}
	for (const [name, raw] of Object.entries(asRecord(app["pools"]) ?? {})) {
		const pool = asRecord(raw);
		if (pool === null) continue;
		const region = pool["region"];
		const placement = typeof region === "string" && region !== "" ? region : BLOB_BUCKET_HOME_PLACEMENT;
		const blobStorage = asRecord(asRecord(pool["infrastructure"])?.["blobStorage"]);
		if (blobStorage !== null) out.push({
			pointer: `/pools/${pointerSegment(name)}/infrastructure/blobStorage`,
			placement,
			blobStorage
		});
		const outOfMesh = pool["workload"] === "out-of-mesh" ? asRecord(pool["blobStorage"]) : null;
		if (outOfMesh !== null) out.push({
			pointer: `/pools/${pointerSegment(name)}/blobStorage`,
			placement,
			blobStorage: outOfMesh
		});
	}
	return out;
}
/** The logical names one binder binds: the keys of the named form, and the legacy `bucket` string. */
function boundNames(binder) {
	const names = Object.keys(asRecord(binder.blobStorage["buckets"]) ?? {});
	const legacy = binder.blobStorage["bucket"];
	if (typeof legacy === "string") names.push(legacy);
	return names;
}
/**
* `blob-bucket-name-unique`: two declarations of one app carry the same `name`.
*
* Input: the bundle; only `app.blobStorage.buckets[]` is read. An `app` that is not a plain object,
* a `buckets` that is not an array, an entry that is not an object and a `name` that is not a string
* give no issue: the schema layer refuses them.
*
* Output: one issue per repeat, at `/blobStorage/buckets/<index>/name` of the repeat, in index
* order. The message names the bucket and the index of its first declaration. Pure; never throws.
*/
function checkBlobBucketNameUnique(bundle) {
	const app = asRecord(bundle.app);
	if (app === null) return [];
	const out = [];
	const first = /* @__PURE__ */ new Map();
	for (const { index, entry } of declaredEntries(app)) {
		const name = entry["name"];
		if (typeof name !== "string") continue;
		const earlier = first.get(name);
		if (earlier === void 0) {
			first.set(name, index);
			continue;
		}
		out.push(issue("blob-bucket-name-unique", `/blobStorage/buckets/${index}/name`, BLOB_BUCKET_RULE_MESSAGES["blob-bucket-name-unique"]({
			bucket: safeRuleLabel(name),
			first: earlier
		})));
	}
	return out;
}
/**
* `blob-bucket-binding-declared`: a fixture or a pool binds a logical bucket that the app does not
* declare.
*
* Input: the bundle. The declared names are the string `name` values of `app.blobStorage.buckets[]`.
* The bound names are the keys of each `blobStorage.buckets` object, in every fixture's
* `infrastructure` (or the deprecated `resources`) block, in every pool's `infrastructure` block,
* and in the top-level `blobStorage` of every out-of-mesh pool.
* The legacy form `blobStorage.bucket` never raises an issue: it is an implicit declaration. A legacy
* name does not declare a bucket for a named binding of another fixture (defaults row F3).
*
* Output: one issue per undeclared name, fixtures in bundle order then pools in key order, names in
* key order. A fixture path is `fixtures/<id>/<infrastructure|resources>/blobStorage/buckets/<name>`,
* prefixed like `pool-image-fixture-unreferenced`; a pool path is
* `/pools/<pool>/infrastructure/blobStorage/buckets/<name>`, or
* `/pools/<pool>/blobStorage/buckets/<name>` for the out-of-mesh key. Every segment passes the M-1
* defence.
* An `app` that is not a plain object gives no issue. Pure; never throws.
*/
function checkBlobBucketBindingDeclared(bundle) {
	const app = asRecord(bundle.app);
	if (app === null) return [];
	const declared = new Set(declaredEntries(app).map(({ entry }) => entry["name"]));
	const out = [];
	for (const binder of binders(bundle, app)) for (const name of Object.keys(asRecord(binder.blobStorage["buckets"]) ?? {})) {
		if (declared.has(name)) continue;
		out.push(issue("blob-bucket-binding-declared", `${binder.pointer}/buckets/${pointerSegment(name)}`, BLOB_BUCKET_RULE_MESSAGES["blob-bucket-binding-declared"]({ bucket: safeRuleLabel(name) })));
	}
	return out;
}
/**
* `blob-bucket-global-requires-right`: a declaration with `scope: 'global'` in an app whose
* `kernelRights` does not name {@link BLOB_BUCKET_GLOBAL_KERNEL_RIGHT} (D3).
*
* Input: `app.blobStorage.buckets[]` and `app.kernelRights`. A `kernelRights` that is absent or not
* an array counts as an empty list. The match is exact and case-sensitive.
*
* Output: one issue per `global` declaration, at `/blobStorage/buckets/<index>/scope`, in index
* order. The message names the bucket. Pure; never throws.
*/
function checkBlobBucketGlobalRequiresRight(bundle) {
	const app = asRecord(bundle.app);
	if (app === null) return [];
	const rights = app["kernelRights"];
	if (Array.isArray(rights) && rights.includes("storage.global")) return [];
	const out = [];
	for (const { index, entry } of declaredEntries(app)) {
		if (entry["scope"] !== "global") continue;
		out.push(issue("blob-bucket-global-requires-right", `/blobStorage/buckets/${index}/scope`, BLOB_BUCKET_RULE_MESSAGES["blob-bucket-global-requires-right"]({
			bucket: safeRuleLabel(entry["name"]),
			right: BLOB_BUCKET_GLOBAL_KERNEL_RIGHT
		})));
	}
	return out;
}
/**
* `blob-bucket-local-single-placement`: a declaration with `locality: 'local'` whose binders do not
* resolve to one placement (section 2.4).
*
* Input: the bundle. A binder of a bucket is a fixture or a pool whose `blobStorage` names it in
* the named form or in the legacy `bucket` form. A pool binds through `infrastructure.blobStorage`.
* An out-of-mesh pool has no `infrastructure` block and binds through its top-level `blobStorage`
* key. The placement of a fixture is
* {@link BLOB_BUCKET_HOME_PLACEMENT}; the placement of a pool is its `region` string, else
* {@link BLOB_BUCKET_HOME_PLACEMENT}. A fixture whose only runnable surface is `poolImage: true`
* adds no placement: it runs only as the image of its pools, and `pool-infrastructure-exceeds-fixture`
* makes it bind every bucket that such a pool binds. Each pool that binds the bucket counts on its
* own. A `local` bucket that no placed binder names has no placement and gives no issue.
*
* Output: one issue per `local` declaration with two or more distinct placements, at
* `/blobStorage/buckets/<index>/locality`, in index order. The message names the bucket and lists
* the placements in sorted order, each through the M-1 defence. Pure; never throws.
*/
function checkBlobBucketLocalSinglePlacement(bundle) {
	const app = asRecord(bundle.app);
	if (app === null) return [];
	const all = binders(bundle, app);
	const out = [];
	for (const { index, entry } of declaredEntries(app)) {
		if (entry["locality"] !== "local") continue;
		const name = entry["name"];
		const placements = /* @__PURE__ */ new Set();
		for (const binder of all) {
			if (binder.placement === null) continue;
			if (boundNames(binder).some((bound) => bound === name)) placements.add(binder.placement);
		}
		if (placements.size < 2) continue;
		const list = [...placements].sort().map((p) => `'${safeRuleLabel(p)}'`).join(", ");
		out.push(issue("blob-bucket-local-single-placement", `/blobStorage/buckets/${index}/locality`, BLOB_BUCKET_RULE_MESSAGES["blob-bucket-local-single-placement"]({
			bucket: safeRuleLabel(name),
			count: placements.size,
			placements: list
		})));
	}
	return out;
}
/**
* `blob-bucket-reserved-name`: an app declaration or a legacy binding names the reserved logical
* name {@link BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME} (2026-10-13-blob-buckets, R-102 item 3).
*
* Why: release-engine adds a `claimcheck` bucket for every app with `platform.eventBus`, with the
* identity (data plane, org, app, `claimcheck`). An app bucket of the same name would have the same
* identity, so the same physical bucket.
*
* Input: the bundle. Two places are read:
* 1. Each `name` of `app.blobStorage.buckets[]` that is exactly `claimcheck`. The match is exact
*    and case-sensitive; a name outside the grammar is the schema layer's refusal.
* 2. Each legacy binding `blobStorage: { bucket: 'claimcheck' }`, in every fixture's
*    `infrastructure` (or the deprecated `resources`) block, in every pool's `infrastructure`
*    block, and in the top-level `blobStorage` of every out-of-mesh pool: a legacy name is an
*    implicit declaration (`resolveFixtureBucketBindings`). The out-of-mesh schema arm refuses the
*    legacy form there, and this rule still reports it.
*
* A named binding `blobStorage.buckets.claimcheck` raises no issue here:
* {@link checkBlobBucketBindingDeclared} refuses it, because no declaration of the name can pass
* this rule. An `app` that is not a plain object reads as an app with no declaration, so the
* fixtures are still read (defaults row F3).
*
* Output: one issue per hit. First the declarations, at `/blobStorage/buckets/<index>/name`, in
* index order. Then the legacy bindings, fixtures in bundle order then pools in key order, at
* `fixtures/<id>/<infrastructure|resources>/blobStorage/bucket`,
* `/pools/<pool>/infrastructure/blobStorage/bucket` or `/pools/<pool>/blobStorage/bucket`. Every
* path segment passes the M-1 defence.
* Pure; never throws.
*/
function checkBlobBucketReservedName(bundle) {
	const app = asRecord(bundle.app) ?? {};
	const message = BLOB_BUCKET_RULE_MESSAGES["blob-bucket-reserved-name"]({ bucket: BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME });
	const out = [];
	for (const { index, entry } of declaredEntries(app)) if (entry["name"] === "claimcheck") out.push(issue("blob-bucket-reserved-name", `/blobStorage/buckets/${index}/name`, message));
	for (const binder of binders(bundle, app)) if (binder.blobStorage["bucket"] === "claimcheck") out.push(issue("blob-bucket-reserved-name", `${binder.pointer}/bucket`, message));
	return out;
}
/**
* Every blob bucket publish rule over one bundle, in the order of {@link BLOB_BUCKET_RULE_IDS}.
* Pure, no I/O, never throws. Every issue carries `severity: 'error'`. `checkAppPools` runs this set
* last, after its own rules, so both publish doors bind it (defaults row F7).
*/
function checkBlobBucketRules(bundle) {
	return [
		...checkBlobBucketNameUnique(bundle),
		...checkBlobBucketBindingDeclared(bundle),
		...checkBlobBucketGlobalRequiresRight(bundle),
		...checkBlobBucketLocalSinglePlacement(bundle),
		...checkBlobBucketReservedName(bundle)
	];
}
/**
* The buckets one fixture binds, each with the declaration it binds (`00-index.md`, WS-1 row).
*
* Callers: the CP allocator (WS-4) and the operator program (WS-5), over a bundle that a publish
* door already accepted. Both take the manifests as they hold them, so the inputs are structural:
* a `FixtureManifest` and an `AppManifest` fit, and so does an opaque manifest record.
*
* Inputs:
* - `fixture`: the fixture manifest. The block is read through `readFixtureInfrastructure`, so the
*   deprecated `resources` spelling counts. Its `blobStorage` is absent, the legacy form or the
*   named form ({@link fixtureBlobStorageBindingSchema}).
* - `appManifest`: the app manifest. Its `blobStorage` is absent or {@link appBlobStorageSchema}.
*
* Output, one row per bound name:
* - no `blobStorage` on the fixture: an empty list.
* - the legacy form `{ bucket, maxSizeGb? }`: one row with `mode: 'readWrite'`. When `app.json`
*   declares `bucket`, the row carries that declaration and `implicit: false`. A legacy `maxSizeGb`
*   then lowers the declared `maxSizeGb` to the smaller of the two (defaults row F1), and sets it
*   when the declaration has none. When `app.json` does not declare `bucket`, the row carries the
*   implicit declaration and `implicit: true`.
* - the named form: one row per key, in the key order of the manifest, each with its mode, its
*   declaration and `implicit: false`.
*
* Errors: throws `TypeError`, with a message that names the field, when the fixture `blobStorage` is
* neither form, when the app `blobStorage` fails {@link appBlobStorageSchema} (a repeated name
* included), or when a named binding names a bucket that `app.json` does not declare (defaults row
* F3, the same refusal as rule `blob-bucket-binding-declared`). The messages carry no
* app-authored value except through `safeRuleLabel`.
*
* Pure: no I/O, and the inputs stay unchanged. The declared names go into a `Map`, so a key such as
* `constructor` never reads the prototype chain. The result and each row are frozen.
*/
function resolveFixtureBucketBindings(fixture, appManifest) {
	const block = asRecord(readFixtureInfrastructure(fixture));
	const rawBinding = block === null ? void 0 : block["blobStorage"];
	if (rawBinding === void 0) return Object.freeze([]);
	const binding = fixtureBlobStorageBindingSchema.safeParse(rawBinding);
	if (!binding.success) throw new TypeError("resolveFixtureBucketBindings: infrastructure.blobStorage is neither the legacy form { bucket } nor the named form { buckets }");
	const declared = /* @__PURE__ */ new Map();
	if (appManifest.blobStorage !== void 0) {
		const app = appBlobStorageSchema.safeParse(appManifest.blobStorage);
		if (!app.success) {
			const first = app.error.issues[0];
			throw new TypeError(`resolveFixtureBucketBindings: app.json blobStorage is invalid at ${safeRuleLabel(first.path.join("."))}: ${safeRuleLabel(first.message)}`);
		}
		for (const declaration of app.data.buckets) declared.set(declaration.name, declaration);
	}
	const data = binding.data;
	if ("bucket" in data) {
		const existing = declared.get(data.bucket);
		if (existing === void 0) {
			const implicit = blobBucketDeclarationSchema.parse(data.maxSizeGb === void 0 ? { name: data.bucket } : {
				name: data.bucket,
				maxSizeGb: data.maxSizeGb
			});
			return Object.freeze([Object.freeze({
				logical: data.bucket,
				mode: "readWrite",
				declaration: implicit,
				implicit: true
			})]);
		}
		const maxSizeGb = data.maxSizeGb === void 0 ? existing.maxSizeGb : existing.maxSizeGb === void 0 ? data.maxSizeGb : Math.min(existing.maxSizeGb, data.maxSizeGb);
		const declaration = maxSizeGb === void 0 ? existing : {
			...existing,
			maxSizeGb
		};
		return Object.freeze([Object.freeze({
			logical: data.bucket,
			mode: "readWrite",
			declaration,
			implicit: false
		})]);
	}
	const out = [];
	for (const [logical, mode] of Object.entries(data.buckets)) {
		const declaration = declared.get(logical);
		if (declaration === void 0) throw new TypeError(`resolveFixtureBucketBindings: infrastructure.blobStorage.buckets binds '${safeRuleLabel(logical)}', which app.json blobStorage.buckets does not declare`);
		out.push(Object.freeze({
			logical,
			mode,
			declaration,
			implicit: false
		}));
	}
	return Object.freeze(out);
}
/**
* The two multi-region locations that the bucket location grammar admits and an org data location
* refuses (2026-10-13-blob-buckets, R-065 item 2, defaults row F1). An org data location is a
* single region. {@link DATA_LOCATION_PATTERN} holds the same refusal in its lookahead.
*/
const DATA_LOCATION_MULTI_REGIONS = ["us", "eu"];
/**
* The grammar of an org data location and of each entry of a data plane's allowed list (R-065
* item 2): {@link BLOB_BUCKET_LOCATION_PATTERN} (a lowercase letter, then 1 to 30 lowercase
* letters, digits or hyphens) minus the exact values of {@link DATA_LOCATION_MULTI_REGIONS}.
*
* The refusal is a lookahead in the pattern and not a zod refinement, so the pattern reaches the
* JSON Schema that `createOpenApiDocument()` emits for a route body that binds
* {@link dataLocationSchema}. The lookahead and the repetition are bounded, so a match runs in time
* linear in the input.
*
* The CHECK `org_placements_data_location_check` of platform-api migration `0014_data_locations.sql`
* holds the bucket location grammar alone. The zod door, not the database, refuses `us` and `eu`.
*/
const DATA_LOCATION_PATTERN = /^(?!(?:us|eu)$)[a-z][a-z0-9-]{1,30}$/;
/** The fewest entries of a data plane's allowed list (R-065 item 2). */
const ALLOWED_LOCATIONS_MIN = 1;
/** The most entries of a data plane's allowed list (R-065 item 2). */
const ALLOWED_LOCATIONS_MAX = 16;
/**
* The reasons {@link resolveDataLocation} gives for a refusal. One today: `location-not-allowed`.
* The release-engine allocators (WS-38) record it as the outcome of an org whose new buckets they
* skip (R-065 item 5).
*/
const DATA_LOCATION_REFUSAL_REASONS = ["location-not-allowed"];
/**
* One org data location: where the new buckets of the org live on its data plane (`overview.md`
* D4, R-065).
*
* Callers: the platform-api doors that write `platform.org_placements.data_location` (WS-15:
* `createOrgSchema`, `assignPlacementSchema` and the body of `PATCH /v1/orgs/:id/data-location`,
* each of which puts it in `.optional()` or `.nullable()` as its door needs), and the cp-admin-ui forms
* (WS-16).
*
* Input: a string in {@link DATA_LOCATION_PATTERN}, for example `us-central1`, `europe-west1` or
* `us-east-1`. Output: the same string, unchanged. The schema has no transform, so it stays
* representable in JSON Schema.
*
* Failure: every other value fails with one issue at the root, among them a multi-region (`us`,
* `eu`), an upper-case letter, a leading digit or hyphen, an underscore, a space, a value of 1 or
* of more than 31 characters, the empty string and a value that is not a string. The schema says
* nothing about whether the provider offers the region, or whether the data plane allows it: the
* allowed list decides that ({@link resolveDataLocation}).
*/
const dataLocationSchema = z.string().regex(DATA_LOCATION_PATTERN, "a single cloud region: lowercase letters, digits and hyphens; us and eu are refused");
/**
* The allowed list of one data plane: the locations an org on that data plane may name as its data
* location (R-065 items 2 and 4).
*
* Callers: provisioning-svc `updateDataPlaneSchema` (WS-43), which writes the list to
* `platform.data_placements.allowed_locations`, and the cp-admin-ui list editor (WS-16).
*
* Input: an array of {@link ALLOWED_LOCATIONS_MIN} to {@link ALLOWED_LOCATIONS_MAX} distinct
* entries, each in {@link dataLocationSchema}. Output: the same array, in the same order.
*
* Failure: an empty array, an array of more than 16 entries, a value that is not an array, an entry
* that fails {@link dataLocationSchema} (the issue path is the entry index) and an entry that equals
* an earlier entry (the issue path is the index of the later one, message `allowed locations must be
* unique`). The schema does not check that the list holds the home region of the data plane or
* that each entry is in the provider region list; WS-43 owns both checks, because only
* provisioning-svc holds the region list (R-065 item 4).
*
* The uniqueness is a refinement. JSON Schema drops it, and the zod door keeps it.
*/
const allowedLocationsSchema = z.array(dataLocationSchema).min(1).max(16).superRefine((value, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	value.forEach((location, index) => {
		if (seen.has(location)) ctx.addIssue({
			code: "custom",
			path: [index],
			message: "allowed locations must be unique"
		});
		seen.add(location);
	});
});
/**
* The location of the new buckets of one org on one data plane (`overview.md` D4, R-065 items 2
* and 5).
*
* Callers: the two release-engine allocators (`PlatformBucketReconcileService` and
* `BlobBucketReconcileService`, WS-38), which pass the result to `ensure()` in place of the data
* plane region, and the platform-api write doors (WS-15), which turn a refusal into a 400.
*
* Rules, in order:
* 1. The effective list is `allowedLocations`, or `[homeRegion]` when it is NULL.
* 2. The candidate is `dataLocation`, or `homeRegion` when it is NULL.
* 3. The answer is `{ ok: true, location: <candidate> }` when the candidate is in
*    {@link dataLocationSchema} and equals an entry of the effective list. The comparison is exact:
*    no case fold and no trim.
* 4. Every other input gives `{ ok: false, reason: 'location-not-allowed' }` (defaults row F3). That
*    covers a location outside the list, a NULL location on a list that lacks the home region, a
*    multi-region or an out-of-grammar value that a raw SQL write stored, and an empty or
*    out-of-grammar home region. A bucket location is never guessed.
*
* Errors: none. The function never throws for an input of its declared type.
*
* Pure: no I/O, no clock. The inputs stay unchanged, and the answer is frozen.
*/
function resolveDataLocation(input) {
	const list = input.allowedLocations ?? [input.homeRegion];
	const candidate = input.dataLocation ?? input.homeRegion;
	if (DATA_LOCATION_PATTERN.test(candidate) && list.includes(candidate)) return Object.freeze({
		ok: true,
		location: candidate
	});
	return Object.freeze({
		ok: false,
		reason: DATA_LOCATION_REFUSAL_REASONS[0]
	});
}
//#endregion
export { ALLOWED_LOCATIONS_MAX, ALLOWED_LOCATIONS_MIN, BLOB_BUCKETS_PER_APP_MAX, BLOB_BUCKET_BROWSER_ORIGINS_MAX, BLOB_BUCKET_BROWSER_ORIGIN_MAX_LENGTH, BLOB_BUCKET_BROWSER_ORIGIN_PATTERN, BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME, BLOB_BUCKET_DELETE_AFTER_DAYS_MAX, BLOB_BUCKET_DURABILITIES, BLOB_BUCKET_GENERATION_MAX, BLOB_BUCKET_GLOBAL_KERNEL_RIGHT, BLOB_BUCKET_HOME_PLACEMENT, BLOB_BUCKET_KINDS, BLOB_BUCKET_LABEL_KEYS, BLOB_BUCKET_LABEL_VALUE_MAX_LENGTH, BLOB_BUCKET_LIFECYCLE_PREFIX_MAX_LENGTH, BLOB_BUCKET_LIFECYCLE_PREFIX_PATTERN, BLOB_BUCKET_LIFECYCLE_RULES_MAX, BLOB_BUCKET_LOCALITIES, BLOB_BUCKET_LOCATION_PATTERN, BLOB_BUCKET_MODES, BLOB_BUCKET_NAME_MAX_LENGTH, BLOB_BUCKET_NAME_MIN_LENGTH, BLOB_BUCKET_NAME_PATTERN, BLOB_BUCKET_PLATFORM_LOGICAL_NAME, BLOB_BUCKET_RULE_IDS, BLOB_BUCKET_RULE_MESSAGES, BLOB_BUCKET_SCOPES, BLOB_BUCKET_STORAGE_CLASSES, DATA_LOCATION_MULTI_REGIONS, DATA_LOCATION_PATTERN, DATA_LOCATION_REFUSAL_REASONS, PHYSICAL_BUCKET_NAME_HASH_LENGTH, PHYSICAL_BUCKET_NAME_PATTERN, PHYSICAL_BUCKET_NAME_PREFIX, PLATFORM_BUCKET_KINDS, allowedLocationsSchema, appBlobStorageSchema, blobBucketBrowserSchema, blobBucketDeclarationSchema, blobBucketKind, blobBucketLabels, blobBucketLifecycleRuleSchema, blobBucketModeSchema, blobBucketNameSchema, checkBlobBucketBindingDeclared, checkBlobBucketGlobalRequiresRight, checkBlobBucketLocalSinglePlacement, checkBlobBucketNameUnique, checkBlobBucketReservedName, checkBlobBucketRules, dataLocationSchema, fixtureBlobStorageBindingSchema, legacyFixtureBlobStorageSchema, namedFixtureBlobStorageSchema, physicalBucketName, resolveDataLocation, resolveFixtureBucketBindings, runsAtHome };

//# sourceMappingURL=blob-buckets.js.map